import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';

interface User {
    id: string;
    email: string | null;
    username?: string | null;
    type: 'admin' | 'client' | 'collaborator' | 'employe';
    name: string;
    clientId?: string | null;
    client_id?: string | null;
    company_name?: string | null;
    logo_url?: string | null;
    logoUrl?: string | null;
    modules_access?: string[];
    isTemporary?: boolean;
    mustChangePassword?: boolean;
    impersonatedBySuperAdmin?: boolean;
    originalAdminEmail?: string | null;
    is_temp_password?: boolean;
    hire_date?: string | null;
    portal_signature_url?: string | null;
    theme_color?: string | null;
    client_type?: 'restauration' | 'prestation' | null;
    prestation_location_mode?: 'domicile' | 'entreprise' | 'les_deux' | null;
    time_clock_enabled?: boolean;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (token: string, user: User) => void;
    logout: () => void;
    refreshUser: () => Promise<void>;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(() => {
        try {
            const raw = localStorage.getItem('user');
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const refreshUser = async () => {
        const me = await authApi.getMe();
        setUser(me);
        localStorage.setItem('user', JSON.stringify(me));
    };

    // Update CSS variables when user changes
    useEffect(() => {
        let brandColor = '#987b55'; // Default IAmani color
        if (user && user.theme_color) {
            brandColor = user.theme_color;
        }
        document.documentElement.style.setProperty('--brand-color', brandColor);

        // Calculate luminance for --brand-fg
        const hex = brandColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16) || 0;
        const g = parseInt(hex.substring(2, 4), 16) || 0;
        const b = parseInt(hex.substring(4, 6), 16) || 0;
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const brandFg = luminance > 0.5 ? '#000000' : '#ffffff';
        document.documentElement.style.setProperty('--brand-fg', brandFg);
    }, [user]);

    useEffect(() => {
        const verifyToken = async () => {
            if (token) {
                try {
                    // Verify with backend
                    await refreshUser();
                } catch (e: any) {
                    console.error('Token verification failed', e);
                    // Only logout if it's a definitive 401/403 or similar
                    // If it's a network error (no status), we keep the current user state to avoid white screen
                    if (
                        e.status === 401 || 
                        e.status === 403 || 
                        (e.message && (e.message.includes('401') || e.message.includes('403') || e.message.includes('Forbidden') || e.message.includes('Unauthorized') || e.message.includes('Session expirée')))
                    ) {
                        logout();
                    }
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        verifyToken();
    }, [token]);

    const login = (newToken: string, newUser: User) => {
        setToken(newToken);
        setUser(newUser);
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(newUser));
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        document.documentElement.style.setProperty('--brand-color', '#987b55');
    };

    return (
        <AuthContext.Provider value={{ user, token, login, logout, refreshUser, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
